import { ChangeDetectionStrategy, Component, Input, OnDestroy, OnInit, SimpleChanges, ViewChild } from '@angular/core';
import { AbstractControl, FormGroup } from '@angular/forms';
import { Observable, of, Subject } from 'rxjs';
import { map, shareReplay, startWith, switchMap, takeUntil, tap } from 'rxjs/operators';
import {
  AutocompleteRemoteComponent,
  AutoCompleteSelectionItem,
  IAutoCompleteService,
} from '../../../core/component/autocomplete-remote/autocomplete-remote.component';
import { FormControlEx } from '../../../core/component/forms/form-control';
import { FormControlExFactory } from '../../../core/service/factory/form-control-ex-factory.service';
import { HelpText } from '../../../core/model/help-text';
import { Address } from '../../model/address';
import { CountryService } from '../../service/data/country.service';
import { LanguageService } from '../../../core/service/language.service';

const formControls = {
  city: 'city',
  country: 'country',
  postalCode: 'postalCode',
  street: 'street',
};

@Component({
  selector: 'app-address',
  templateUrl: './address.component.html',
  styleUrls: ['./address.component.scss'],
  providers: [CountryService],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class AddressComponent implements OnInit, OnDestroy, IAutoCompleteService {
  constructor(
    private readonly _controlModelFactory: FormControlExFactory,
    private readonly _countryService: CountryService,
    private readonly _languageService: LanguageService
  ) {
    this.title = 'ADDRESS_FORM.HEADER';
  }

  getSelectionItem(id: string): Observable<AutoCompleteSelectionItem> {
    if (!id) {
      return of(null);
    }

    return this._countries.pipe(map(x => x.find(c => c.value === id)));
  }

  getSelection(filter: string): Observable<AutoCompleteSelectionItem[]> {
    return this._countries.pipe(
      map(countries => {
        if (filter === '*') {
          return countries;
        }

        let bestMatches = countries.filter(c => c.name.toLowerCase().indexOf(filter.toLowerCase()) == 0);
        for (let match of countries.filter(c => c.name.toLowerCase().indexOf(filter.toLowerCase()) > 0)) {
          bestMatches.push(match);
        }

        return bestMatches;
      })
    );
  }

  get countryService(): IAutoCompleteService {
    return this;
  }

  ngOnDestroy(): void {
    this._destroy.next();
  }

  // Accept AbstractControl for template binding; used internally as a FormGroup.
  private _group: FormGroup;
  @Input() set group(value: AbstractControl) {
    this._group = value as FormGroup;
  }
  get group(): FormGroup {
    return this._group;
  }
  @Input() helpText: string | HelpText;
  get resolvedHelpText(): string {
    return typeof this.helpText === 'string' ? this.helpText : this.helpText?.toString();
  }
  @Input() mandatory: boolean;
  // RentalObject.address is typed `string | Address`; in these forms it is always
  // an Address at runtime. Accept the union, expose/use as Address.
  private _model: Address;
  @Input() set model(value: string | Address) {
    this._model = value as Address;
  }
  get model(): Address {
    return this._model;
  }
  @Input() title: string;
  /** Allows constrained containers, such as sidebars, to give the country selector more room. */
  @Input() countryColumnSpan = 1;
  @ViewChild('countyinput') countyinput: AutocompleteRemoteComponent;

  private readonly _destroy = new Subject<void>();
  private readonly _countries = this._languageService.onLangChange.pipe(
    startWith(null),
    switchMap(t =>
      this._countryService.get().pipe(
        map(res => res.map(c => new AutoCompleteSelectionItem(c.name, c.value, c))),
        tap(res => {
          if (t !== null && this.country.value) {
            const a = res.find(x => x.value === this.country.value);
            this.countyinput.setControlValue(a);
          }
        })
      )
    ),
    takeUntil(this._destroy),
    shareReplay({ bufferSize: 1, refCount: false })
  );

  get city(): FormControlEx {
    return this.group.controls[formControls.city] as FormControlEx;
  }

  get country(): FormControlEx {
    return this.group.controls[formControls.country] as FormControlEx;
  }

  get postalCode(): FormControlEx {
    return this.group.controls[formControls.postalCode] as FormControlEx;
  }

  get street(): FormControlEx {
    return this.group.controls[formControls.street] as FormControlEx;
  }

  get resolvedCountryColumnSpan(): number {
    return Math.min(Math.max(this.countryColumnSpan, 1), 3);
  }

  get resolvedCityColumnSpan(): number {
    return 4 - this.resolvedCountryColumnSpan;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['mandatory'] && !changes['mandatory'].firstChange) {
      const controlModelNames = [formControls.city, formControls.country, formControls.postalCode, formControls.street];
      controlModelNames.forEach(name => ((<FormControlEx>this.group.controls[name]).required = changes['mandatory'].currentValue));
    }
  }

  ngOnInit() {
    this.group.addControl(
      formControls.city,
      this._controlModelFactory.createWithOptions('ADDRESS_FORM.CITY_LABEL', this.model.city, {
        length: 50,
        required: this.mandatory,
      })
    );
    this.group.addControl(
      formControls.country,
      this._controlModelFactory.createWithOptions('ADDRESS_FORM.COUNTRY_LABEL', this.model.country, {
        length: 50,
        required: this.mandatory,
      })
    );
    this.group.addControl(
      formControls.postalCode,
      this._controlModelFactory.createWithOptions('ADDRESS_FORM.POSTAL_CODE_LABEL', this.model.postalCode, {
        length: 50,
        required: this.mandatory,
      })
    );
    this.group.addControl(
      formControls.street,
      this._controlModelFactory.createWithOptions('ADDRESS_FORM.STREET_LABEL', this.model.street, {
        length: 50,
        required: this.mandatory,
      })
    );
  }
}
